import { type LanguageWithOther } from './language.js';
import { type Specialty } from '../common.js';
import { type Geometry } from '../places.js';
import type * as Practice from '../practice.js';
import type * as ProviderOrganizationPreference from '../providerOrganizationPreference.js';
import type * as Profile from '../providerProfile.js';
import * as User from '../user.js';

export type SupportedSexMap = {
  [key in User.Sex]?: number;
};

export type SupportedLanguageMap = {
  [key in LanguageWithOther]?: number;
};

export type SupportedPractice = Pick<Practice.Practice, 'id' | 'name'> & {
  count: number;
};

export type SupportedProviderTypeMap = {
  [key in ProviderType]?: number;
};

export type SupportedLocationMap = Record<string, number>;

export enum ProviderType {
  Physician = 'PHYSICIAN',
  PhysicianAssistant = 'PHYSICIAN_ASSISTANT',
  NursePractitioner = 'NURSE_PRACTITIONER',
}

export type ProviderTypeMap = {
  [key in ProviderType]: (User.DrSuffixMap | User.OtherSuffixMap)[];
};

export const providerTypeMapping: ProviderTypeMap = {
  PHYSICIAN: [
    User.DrSuffixMap.MD,
    User.DrSuffixMap.DO,
    User.DrSuffixMap.PsyD,
    User.DrSuffixMap.PHD,
  ],
  PHYSICIAN_ASSISTANT: [User.OtherSuffixMap.PA, User.OtherSuffixMap.PAC],
  NURSE_PRACTITIONER: [User.OtherSuffixMap.NP, User.OtherSuffixMap.DNP],
};

export const providerTypeMap: {
  [providerType in ProviderType]: string;
} = {
  PHYSICIAN: 'Physician',
  PHYSICIAN_ASSISTANT: 'Physician Assistant',
  NURSE_PRACTITIONER: 'Nurse Practitioner',
};

export interface RatingWithCount {
  value: number;
  count: number;
}

export interface ProviderRating {
  reputation: RatingWithCount;
}

export type SafeProviderWithPractices = User.SafeProvider & {
  practices: Practice.SafePracticeWithDistance[];
  profile?: Profile.Profile | null;
  preference?: ProviderOrganizationPreference.Preference | null;
  rating?: ProviderRating | null;
  id: string;
};

export interface ProvidersWithSupportedValues {
  providers: SafeProviderWithPractices[];
  supportedLanguages: SupportedLanguageMap;
  supportedLocations: SupportedLocationMap;
  supportedPractices: SupportedPractice[];
  supportedProviderTypes: SupportedProviderTypeMap;
  supportedSexes: SupportedSexMap;
  supportedSpecialties: Specialty[];
  acceptedPayorWithPlanTypes: string[];
}

interface FilterObject {
  specialty: string;
  specialties: Specialty[];
  geometry: Geometry;
  spokenLanguages: LanguageWithOther[];
  sexes: User.Sex[];
  offersVirtualVisits: boolean;
  offersOnlineScheduling: boolean;
  acceptsNewPatients: boolean;
  withinRadiusMiles: number;
  practiceIds: string[];
  providerTypes: ProviderType[];
  payorWithPlanTypes: string[];
  isPrimaryCareProvider: boolean;
  agesServed: ProviderOrganizationPreference.AgesServed[];
  locations: string[];
  ableToSchedule: boolean;
  keywords: string[];
  providers: string[];
}

export type FilterOptions = Partial<FilterObject>;
